import { parse, stringify } from "devalue";
import type { ChatRpcHandlers } from "./chat-rpc.server";

export const CHAT_RPC_HEADER = "x-hiive-chat-rpc";

type ChatRpcName = keyof ChatRpcHandlers;

/**
 * Calls a hot chat action over `/api/chat/rpc` rather than as a Server Action,
 * so it never waits in Next's one-at-a-time action queue. See
 * `CHAT_RPC_HANDLERS` for why.
 */
async function chatRpc<K extends ChatRpcName>(
  fn: K,
  ...args: Parameters<ChatRpcHandlers[K]>
): Promise<Awaited<ReturnType<ChatRpcHandlers[K]>>> {
  // The query fns also seed server-rendered pages (the chat list), where there
  // is no action queue to dodge and no origin to fetch against — call through.
  if (typeof window === "undefined") {
    const actions = await import("@/actions/chat");
    const action = actions[fn] as (...input: unknown[]) => Promise<unknown>;
    return (await action(...args)) as Awaited<ReturnType<ChatRpcHandlers[K]>>;
  }
  const response = await fetch("/api/chat/rpc", {
    method: "POST",
    credentials: "same-origin",
    cache: "no-store",
    headers: {
      "content-type": "text/plain;charset=UTF-8",
      [CHAT_RPC_HEADER]: "1",
    },
    body: stringify({ fn, args }),
  });
  if (!response.ok) {
    throw new Error(`Chat request failed (${response.status})`);
  }
  return parse(await response.text());
}

/** Drop-in, same-named replacements for the `@/actions/chat` exports. */
export const getChatMessages = (
  ...args: Parameters<ChatRpcHandlers["getChatMessages"]>
) => chatRpc("getChatMessages", ...args);
export const getChatMessagesDelta = (
  ...args: Parameters<ChatRpcHandlers["getChatMessagesDelta"]>
) => chatRpc("getChatMessagesDelta", ...args);
export const getTotalUnreadChatCount = () => chatRpc("getTotalUnreadChatCount");
export const getTypingUsers = (
  ...args: Parameters<ChatRpcHandlers["getTypingUsers"]>
) => chatRpc("getTypingUsers", ...args);
export const getUserChats = (
  ...args: Parameters<ChatRpcHandlers["getUserChats"]>
) => chatRpc("getUserChats", ...args);
export const getUserChatsDelta = (
  ...args: Parameters<ChatRpcHandlers["getUserChatsDelta"]>
) => chatRpc("getUserChatsDelta", ...args);
export const getUsersPresence = (
  ...args: Parameters<ChatRpcHandlers["getUsersPresence"]>
) => chatRpc("getUsersPresence", ...args);
export const heartbeatChatPresence = () => chatRpc("heartbeatChatPresence");
export const markChatSeen = (
  ...args: Parameters<ChatRpcHandlers["markChatSeen"]>
) => chatRpc("markChatSeen", ...args);
export const sendMessage = (
  ...args: Parameters<ChatRpcHandlers["sendMessage"]>
) => chatRpc("sendMessage", ...args);
export const setTyping = (...args: Parameters<ChatRpcHandlers["setTyping"]>) =>
  chatRpc("setTyping", ...args);
